import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useAccount, useConnect, useSwitchChain } from "wagmi";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { BaseWalletMultiButton } from "@solana/wallet-adapter-react-ui";
import {
  PublicKey,
  VersionedTransaction,
  TransactionMessage,
  Connection,
  Transaction,
} from "@solana/web3.js";
import { getAssociatedTokenAddress, getAccount } from "@solana/spl-token";
import { ethers } from "ethers";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Buffer } from "buffer";
import bs58 from "bs58";
import "../App.css";
import "@solana/wallet-adapter-react-ui/styles.css";

// Ensure environment variables are set
if (!process.env.REACT_APP_API_KEY || !process.env.REACT_APP_FRONTEND_SECRET) {
  throw new Error("Missing required environment variables: REACT_APP_API_KEY or REACT_APP_FRONTEND_SECRET");
}

// Secure Axios instance with default headers
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "https://api.bescbridge.com",
  headers: {
    "x-api-key": process.env.REACT_APP_API_KEY,
    "x-besc-frontend": process.env.REACT_APP_FRONTEND_SECRET,
    "Content-Type": "application/json",
  },
});

// Add nonce and timestamp to each request
apiClient.interceptors.request.use((config) => {
  config.headers["x-nonce"] = ethers.utils.hexlify(ethers.utils.randomBytes(32));
  config.headers["x-timestamp"] = Date.now().toString();
  return config;
});

// Handle API errors securely
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API error:', error.response?.status, error.response?.data?.error || error.message);
    throw new Error(error.response?.data?.error || 'API request failed');
  }
);

window.Buffer = Buffer;

const CENTRAL_POOL_ADDRESS = "0xE59C43191C8754658BB21cE919FEFAcb4eA0B474";
const BSC_CHAIN_ID = 56;
const BESC_CHAIN_ID = 2372;
const BUSDC_ADDRESS = "0xB54aD626E127f0f228dBeab6F2A61e8e6e029A4B";
const BSC_USDC_ADDRESS = "0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d";
const DEFAULT_SOLANA_RPC_ENDPOINTS = [
  "https://api.mainnet-beta.solana.com",
  "https://rpc.ankr.com/solana",
  "https://solana-rpc.publicnode.com",
];

const ENV_SOLANA_RPC_ENDPOINTS = (process.env.REACT_APP_SOLANA_RPC_ENDPOINTS || "")
  .split(",")
  .map((value) => value.trim())
  .filter(Boolean);

const RPC_ENDPOINT_COOLDOWN_MS = 10 * 60 * 1000;

const buildSolanaRpcEndpoints = (preferredEndpoint) =>
  Array.from(
    new Set(
      [preferredEndpoint, ...ENV_SOLANA_RPC_ENDPOINTS, ...DEFAULT_SOLANA_RPC_ENDPOINTS].filter(
        Boolean
      )
    )
  );

// Validate constant addresses
const validateAddresses = () => {
  const invalidAddresses = [];
  if (!ethers.utils.isAddress(CENTRAL_POOL_ADDRESS))
    invalidAddresses.push(`CENTRAL_POOL_ADDRESS=${CENTRAL_POOL_ADDRESS}`);
  if (!ethers.utils.isAddress(BUSDC_ADDRESS))
    invalidAddresses.push(`BUSDC_ADDRESS=${BUSDC_ADDRESS}`);
  if (!ethers.utils.isAddress(BSC_USDC_ADDRESS))
    invalidAddresses.push(`BSC_USDC_ADDRESS=${BSC_USDC_ADDRESS}`);
  if (invalidAddresses.length > 0)
    throw new Error(`Invalid address: ${invalidAddresses.join(", ")}`);
};
validateAddresses();

const CHAIN_CONFIGS = {
  "2": {
    name: "Ethereum",
    wagmiAddressId: 1,
    nativeSymbol: "ETH",
    debridgeChainId: 1,
    usdcAddress: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
    decimals: 6,
    dlnSourceAddress: "0xef4fb24ad0916217251f553c0596f8edc630eb66",
  },
  "4": {
    name: "BNB Chain",
    wagmiAddressId: 56,
    nativeSymbol: "BNB",
    debridgeChainId: 56,
    usdcAddress: "0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d",
    decimals: 18,
    dlnSourceAddress: "0xef4fb24ad0916217251f553c0596f8edc630eb66",
  },
  "5": {
    name: "Polygon",
    wagmiAddressId: 137,
    nativeSymbol: "POL",
    debridgeChainId: 137,
    usdcAddress: "0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359",
    decimals: 6,
    dlnSourceAddress: "0xef4fb24ad0916217251f553c0596f8edc630eb66",
  },
  "30": {
    name: "Base",
    wagmiAddressId: 8453,
    nativeSymbol: "ETH",
    debridgeChainId: 8453,
    usdcAddress: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
    decimals: 6,
    dlnSourceAddress: "0xef4fb24ad0916217251f553c0596f8edc630eb66",
  },
  "23": {
    name: "Arbitrum",
    wagmiAddressId: 42161,
    nativeSymbol: "ETH",
    debridgeChainId: 42161,
    usdcAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    decimals: 6,
    dlnSourceAddress: "0xef4fb24ad0916217251f553c0596f8edc630eb66",
  },
  "24": {
    name: "Optimism",
    wagmiAddressId: 10,
    nativeSymbol: "ETH",
    debridgeChainId: 10,
    usdcAddress: "0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85",
    decimals: 6,
    dlnSourceAddress: "0xef4fb24ad0916217251f553c0596f8edc630eb66",
  },
  "6": {
    name: "Avalanche",
    wagmiAddressId: 43114,
    nativeSymbol: "AVAX",
    debridgeChainId: 43114,
    usdcAddress: "0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E",
    decimals: 6,
    dlnSourceAddress: "0xef4fb24ad0916217251f553c0596f8edc630eb66",
  },
  "1": {
    name: "Solana",
    debridgeChainId: 7565164,
    usdcAddress: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v",
    decimals: 6,
    isSolana: true,
    dlnSourceProgramId: "src5qyZHqTqecJV4aY6Cb6zDZLMDzrDKKezs22MPHr4",
    dlnDestinationProgramId: "dst5MGcFPoBeREFAA5E3tU5ij8m5uVYwkzkSAbsLbNo",
  },
  "2372": {
    name: "BESC HyperChain",
    wagmiAddressId: 2372,
    busdcAddress: "0xB54aD626E127f0f228dBeab6F2A61e8e6e029A4B",
    decimals: 18,
  },
};

const TOKEN_SYMBOLS = {
  toBESC: { source: "USDC", dest: "BUSDC" },
  fromBESC: { source: "BUSDC", dest: "USDC" },
};

const erc20Abi = [
  {
    constant: false,
    inputs: [
      { name: "spender", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    name: "approve",
    outputs: [{ name: "success", type: "bool" }],
    type: "function",
  },
  {
    constant: true,
    inputs: [
      { name: "owner", type: "address" },
      { name: "spender", type: "address" },
    ],
    name: "allowance",
    outputs: [{ name: "", type: "uint256" }],
    type: "function",
  },
  {
    constant: true,
    inputs: [{ name: "_owner", type: "address" }],
    name: "balanceOf",
    outputs: [{ name: "balance", type: "uint256" }],
    type: "function",
  },
  {
    constant: false,
    inputs: [
      { name: "recipient", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    name: "transfer",
    outputs: [{ name: "success", type: "bool" }],
    type: "function",
  },
];

const DLN_ERROR_MESSAGES = {
  IMPOSSIBLE_ROUTE: "No valid route found for this bridge. Try a different chain or pair.",
  INSUFFICIENT_LIQUIDITY: "Not enough liquidity for this transaction. Try a smaller amount.",
  INVALID_RECIPIENT: "Invalid recipient address. Please check the destination address.",
  INSUFFICIENT_AMOUNT: "Amount too low for bridging. Please increase the amount.",
  UNKNOWN_ERROR: "An unexpected error occurred. Please try again or contact support.",
  ESTIMATION_FAILED: "Transaction estimation failed. Check your wallet balances or try a smaller amount.",
};

const SOLANA_BUTTON_LABELS = {
  "change-wallet": "Change wallet",
  connecting: "Connecting ...",
  "copy-address": "Copy address",
  copied: "Copied",
  disconnect: "Disconnect",
  "has-wallet": "Connect Phantom",
  "no-wallet": "Connect Phantom",
};

const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const withTimeout = async (promise, ms, errorMsg = "Operation timed out") => {
  const timeout = new Promise((_, reject) =>
    setTimeout(() => reject(new Error(errorMsg)), ms)
  );
  return Promise.race([promise, timeout]);
};

const validateEvmTxHash = (hash) => ethers.utils.isHexString(hash, 32);
const validateSolanaTxHash = (hash) => /^[1-9A-HJ-NP-Za-km-z]{80,90}$/.test(hash);

const isRpcAuthError = (error) => {
  const status = error?.response?.status;
  const message = `${error?.message || ""} ${error?.toString?.() || ""}`.toLowerCase();
  return (
    status === 401 ||
    status === 403 ||
    message.includes(" 401") ||
    message.includes(" 403") ||
    message.includes("forbidden") ||
    message.includes("unauthorized")
  );
};

const isRpcQuotaError = (error) => {
  const status = error?.response?.status;
  const code = error?.code ?? error?.cause?.code ?? error?.error?.code;
  const message = `${error?.message || ""} ${error?.toString?.() || ""}`.toLowerCase();
  return (
    status === 429 ||
    code === 10 ||
    code === 429 ||
    message.includes("user balance exceeded") ||
    message.includes("rate limit") ||
    message.includes("too many requests") ||
    message.includes("code\":10")
  );
};

const isNonRetryableRpcError = (error) => isRpcAuthError(error) || isRpcQuotaError(error);

const withRetry = async (fn, maxAttempts = 5, delayMs = 1000, shouldRetry = () => true) => {
  let lastError;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt < maxAttempts && shouldRetry(error))
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      else throw error;
    }
  }
  throw lastError;
};

const getErrorMessage = (error) => {
  const data = error?.response?.data;
  if (data?.error) return data.error;
  // deBridge DLN API errors: { errorCode, errorId, errorMessage }
  if (data?.errorId || data?.errorMessage) {
    const friendly = DLN_ERROR_MESSAGES[data.errorId];
    const detail = data.errorMessage ? ` (${data.errorMessage})` : "";
    return friendly ? `${friendly}${detail}` : `deBridge error ${data.errorId || ""}${detail}`;
  }
  // express-validator errors from our backend: { errors: [{ msg, path }] }
  if (Array.isArray(data?.errors) && data.errors.length) {
    return data.errors.map((e) => (e.path ? `${e.path}: ${e.msg}` : e.msg)).join("; ");
  }
  if (error?.message) return error.message;
  return "An unexpected error occurred";
};

const BridgeWidget = () => {
  const { address: evmAddress, isConnected: isEvmConnected } = useAccount();
  const { connect: connectEvm, connectors } = useConnect();
  const { switchChain, isLoading: switchChainLoading } = useSwitchChain();
  const { connection } = useConnection();
  const {
    publicKey: solanaAddressRaw,
    connect: connectSolana,
    disconnect,
    connected: isSolanaConnected,
    signTransaction,
    wallet,
  } = useWallet();
  const [solanaAddress, setSolanaAddress] = useState("");
  const [walletChainId, setWalletChainId] = useState(null);
  const [usdcBalance, setUsdcBalance] = useState(null);
  const [solanaUsdcBalance, setSolanaUsdcBalance] = useState(null);
  const [solanaSolBalance, setSolanaSolBalance] = useState(null);
  const [isBalanceLoading, setIsBalanceLoading] = useState(false);
  const [direction, setDirection] = useState("toBESC");
  const [sourceChainId, setSourceChainId] = useState("");
  const [destinationChainId, setDestinationChainId] = useState(direction === "toBESC" ? "2372" : "");
  const [amount, setAmount] = useState("");
  const [fee, setFee] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [bridgeStatus, setBridgeStatus] = useState("");
  const [txHash, setTxHash] = useState("");
  const [jobId, setJobId] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("connected");
  const [poolStatus, setPoolStatus] = useState({
    usdcBalance: 0,
    withdrawalsEnabled: false,
    amountThreshold: 1000,
  });
  const [lastBalanceFetch, setLastBalanceFetch] = useState(0);
  const [bescAddress, setBescAddress] = useState("");
  const [bescAddressError, setBescAddressError] = useState("");
  const [solanaAddressInput, setSolanaAddressInput] = useState("");
  const [solanaAddressError, setSolanaAddressError] = useState("");
  const [showProgressModal, setShowProgressModal] = useState(false);
  const rpcEndpointCooldownsRef = useRef(new Map());

  const markRpcEndpointCoolingDown = useCallback((endpoint) => {
    if (!endpoint) return;
    rpcEndpointCooldownsRef.current.set(endpoint, Date.now() + RPC_ENDPOINT_COOLDOWN_MS);
  }, []);

  const getHealthySolanaRpcEndpoints = useCallback(() => {
    const now = Date.now();
    return buildSolanaRpcEndpoints(connection?.rpcEndpoint).filter((endpoint) => {
      const cooldownUntil = rpcEndpointCooldownsRef.current.get(endpoint);
      return !cooldownUntil || cooldownUntil <= now;
    });
  }, [connection]);

  useEffect(() => {
    if (!isSolanaConnected || !solanaAddressRaw) {
      setSolanaAddress("");
      return;
    }
    const currentAddressRaw = solanaAddressRaw;
    const currentIsConnected = isSolanaConnected;
    const timer = setTimeout(() => {
      if (!currentIsConnected || !currentAddressRaw) {
        setSolanaAddress("");
        setError("Wallet disconnected. Please reconnect your Solana wallet.");
        return;
      }
      try {
        if (typeof currentAddressRaw === "string") {
          new PublicKey(currentAddressRaw);
          setSolanaAddress(currentAddressRaw);
        } else if (currentAddressRaw instanceof PublicKey) {
          setSolanaAddress(currentAddressRaw.toBase58());
        } else {
          setSolanaAddress("");
          setError("Unexpected wallet state. Please reconnect your Solana wallet.");
        }
      } catch (err) {
        setSolanaAddress("");
        setError("Invalid Solana wallet address. Please reconnect your wallet.");
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [solanaAddressRaw, isSolanaConnected]);

  useEffect(() => {
    setConnectionStatus(
      isSolanaConnected && sourceChainId === "1" && solanaAddress
        ? "Connected"
        : "Disconnected"
    );
    if (isSolanaConnected && solanaAddress && sourceChainId === "1") setError("");
  }, [isSolanaConnected, solanaAddress, sourceChainId]);

  const fetchWalletChainId = async () => {
    if (!window.ethereum || sourceChainId === "1") return null;
    try {
      const chainIdHex = await window.ethereum.request({ method: "eth_chainId" });
      const chainIdDecimal = parseInt(chainIdHex, 16);
      const chainConfigEntry = Object.entries(CHAIN_CONFIGS).find(
        ([, config]) => config.wagmiAddressId === chainIdDecimal && !config.isSolana
      );
      return chainConfigEntry ? parseInt(chainConfigEntry[0]) : null;
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    const initializeChainId = async () => {
      if (sourceChainId !== "1") {
        const chainId = await fetchWalletChainId();
        setWalletChainId(chainId);
        if (!sourceChainId && chainId) setSourceChainId(chainId.toString());
      } else {
        setWalletChainId(null);
      }
    };
    if (isEvmConnected && !walletChainId && sourceChainId !== "1")
      initializeChainId();
  }, [isEvmConnected, walletChainId, sourceChainId]);

  useEffect(() => {
    const fetchPoolStatus = async () => {
      try {
        const response = await withTimeout(apiClient.get("/pool-status"), 5000);
        setPoolStatus(response.data);
      } catch (err) {
        setError("Failed to fetch pool status. Please try again later.");
      }
    };
    fetchPoolStatus();
    const interval = setInterval(fetchPoolStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const a = parseFloat(amount) || 0;
    const bridgeFee = a * 0.0025;
    const operatorFee = Math.max(5, a * 0.01);
    const poolGasFee = 0.5;
    const totalFee = bridgeFee + operatorFee + poolGasFee;
    setFee(totalFee);
    setTotalAmount(a + totalFee);
  }, [amount, direction]);

  useEffect(() => {
    const fetchBalance = async () => {
      if (
        !evmAddress ||
        !sourceChainId ||
        !CHAIN_CONFIGS?.[parseInt(sourceChainId)] ||
        sourceChainId === "1"
      ) {
        setUsdcBalance(0);
        setIsBalanceLoading(false);
        return;
      }
      if (!window.ethereum) {
        setUsdcBalance(0);
        setIsBalanceLoading(false);
        return;
      }
      setIsBalanceLoading(true);
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const config = CHAIN_CONFIGS[parseInt(sourceChainId)];
      const usdcAddr = direction === "toBESC" ? config.usdcAddress : config.busdcAddress;
      const decimals = config.decimals || 6;
      try {
        if (!usdcAddr)
          throw new Error(`USDC/BUSDC address not defined for chain: ${sourceChainId}`);
        const contract = new ethers.Contract(usdcAddr, erc20Abi, provider);
        const rawBalance = await withTimeout(
          contract.balanceOf(evmAddress),
          5000
        );
        const bal = parseFloat(ethers.utils.formatUnits(rawBalance, decimals));
        setUsdcBalance(bal);
      } catch (err) {
        setUsdcBalance(0);
      } finally {
        setIsBalanceLoading(false);
      }
    };
    fetchBalance();
  }, [evmAddress, sourceChainId, direction]);

  const fetchSolanaBalances = useCallback(
    debounce(async () => {
      const now = Date.now();
      if (now - lastBalanceFetch < 60000) return;
      setLastBalanceFetch(now);
      if (
        !solanaAddress ||
        sourceChainId !== "1" ||
        !isSolanaConnected ||
        !solanaAddressRaw
      ) {
        setSolanaUsdcBalance(null);
        setSolanaSolBalance(null);
        setIsBalanceLoading(false);
        return;
      }
      setIsBalanceLoading(true);
      const endpoints = getHealthySolanaRpcEndpoints();
      if (!endpoints.length) {
        setSolanaUsdcBalance(0);
        setSolanaSolBalance(0);
        setIsBalanceLoading(false);
        setError("No available Solana RPC endpoint. Please retry in a few minutes.");
        return;
      }
      const tryRpc = async (rpcUrl) => {
        try {
          return new Connection(rpcUrl, "confirmed");
        } catch (error) {
          throw error;
        }
      };
      let solanaPublicKey;
      try {
        solanaPublicKey = new PublicKey(solanaAddress);
      } catch (err) {
        setSolanaUsdcBalance(0);
        setSolanaSolBalance(0);
        setIsBalanceLoading(false);
        setError("Invalid Solana address. Please reconnect your wallet.");
        return;
      }
      try {
        const usdcMint = new PublicKey(CHAIN_CONFIGS[1].usdcAddress);
        const associatedTokenAddress = await getAssociatedTokenAddress(
          usdcMint,
          solanaPublicKey
        );
        for (const endpoint of endpoints) {
          try {
            const currentConn = await tryRpc(endpoint);
            const account = await withTimeout(
              withRetry(
                () => getAccount(currentConn, associatedTokenAddress),
                5,
                1000,
                (error) => !isNonRetryableRpcError(error)
              ),
              5000
            );
            const usdcBalance =
              Number(account.amount) / Math.pow(10, CHAIN_CONFIGS[1].decimals);
            setSolanaUsdcBalance(usdcBalance);
            break;
          } catch (error) {
            if (isNonRetryableRpcError(error)) markRpcEndpointCoolingDown(endpoint);
            if (endpoint === endpoints[endpoints.length - 1]) setSolanaUsdcBalance(0);
          }
        }
      } catch (error) {
        setSolanaUsdcBalance(0);
      }
      try {
        for (const endpoint of endpoints) {
          try {
            const currentConn = await tryRpc(endpoint);
            const solBalance = await withTimeout(
              withRetry(
                () => currentConn.getBalance(solanaPublicKey),
                5,
                1000,
                (error) => !isNonRetryableRpcError(error)
              ),
              5000
            );
            setSolanaSolBalance(solBalance / 1e9);
            break;
          } catch (error) {
            if (isNonRetryableRpcError(error)) markRpcEndpointCoolingDown(endpoint);
            if (endpoint === endpoints[endpoints.length - 1]) setSolanaSolBalance(0);
          }
        }
      } catch (error) {
        setSolanaSolBalance(0);
      } finally {
        setIsBalanceLoading(false);
      }
    }, 500),
    [
      solanaAddress,
      sourceChainId,
      isSolanaConnected,
      solanaAddressRaw,
      lastBalanceFetch,
      getHealthySolanaRpcEndpoints,
      markRpcEndpointCoolingDown,
    ]
  );

  useEffect(() => {
    if (isSolanaConnected && solanaAddress && sourceChainId === "1" && solanaAddressRaw)
      fetchSolanaBalances();
    else {
      setSolanaUsdcBalance(null);
      setSolanaSolBalance(null);
    }
  }, [solanaAddress, sourceChainId, isSolanaConnected, solanaAddressRaw, fetchSolanaBalances]);

  useEffect(() => {
    let interval;
    if (jobId) {
      interval = setInterval(async () => {
        try {
          const response = await withTimeout(
            apiClient.get(`/job-status/${jobId}`),
            5000
          );
          const { status, result, error } = response.data;
          if (status === "completed") {
            setBridgeStatus(
              <span>
                Success! BUSDC Minted on BESC:{' '}
                <a
                  href={`https://explorer.beschyperchain.com/tx/${result.bescTxHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {result.bescTxHash}
                </a>
              </span>
            );
            setTxHash(result.bescTxHash);
            setJobId("");
          } else if (status === "failed") {
            setError(`Bridge failed: ${error}`);
            setBridgeStatus("");
            setJobId("");
          } else {
            setBridgeStatus(`Processing bridge transaction (Job ID: ${jobId})...`);
          }
        } catch (err) {
          setError("Failed to check bridge status. Retrying...");
        }
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [jobId]);

  const approveBusdc = async (amount) => {
    if (!window.ethereum) throw new Error('No EVM wallet detected');
    const provider = new ethers.providers.Web3Provider(window.ethereum);
    const signer = provider.getSigner();
    const contract = new ethers.Contract(BUSDC_ADDRESS, erc20Abi, signer);
    const decimals = CHAIN_CONFIGS[BESC_CHAIN_ID].decimals;
    const amountInUnits = ethers.utils.parseUnits(amount.toString(), decimals);
    setBridgeStatus('Checking BUSDC allowance...');
    const allowance = await contract.allowance(evmAddress, CENTRAL_POOL_ADDRESS);
    if (allowance.lt(amountInUnits)) {
      setBridgeStatus(`Approving ${amount} BUSDC...`);
      const gasPrice = await provider.getGasPrice();
      const approveTx = await contract.approve(CENTRAL_POOL_ADDRESS, amountInUnits, {
        gasLimit: 100000,
        gasPrice,
      });
      await approveTx.wait();
      setBridgeStatus('BUSDC approval successful');
    } else {
      setBridgeStatus('Sufficient BUSDC allowance already set');
    }
    const newAllowance = await contract.allowance(evmAddress, CENTRAL_POOL_ADDRESS);
    if (newAllowance.lt(amountInUnits)) {
      throw new Error(
        `BUSDC allowance not set correctly: ${ethers.utils.formatUnits(
          newAllowance,
          decimals
        )} BUSDC approved`
      );
    }
  };

  const verifyEvmBridgeOutput = async (txHash, totalAmount, sourceChainId) => {
    if (!validateEvmTxHash(txHash)) throw new Error(`Invalid EVM transaction hash: ${txHash}`);
    const bscProvider = new ethers.providers.JsonRpcProvider("https://bsc-dataseed.binance.org/");
    const usdcContract = new ethers.Contract(
      BSC_USDC_ADDRESS,
      ["event Transfer(address indexed from, address indexed to, uint256 value)"],
      bscProvider
    );
    const decimals = CHAIN_CONFIGS[4].decimals;
    const maxAttempts = 10;
    let initialPoolBalance = null;
    try {
      const poolResponse = await withTimeout(apiClient.get("/pool-status"), 5000);
      initialPoolBalance = parseFloat(poolResponse.data.usdcBalance);
    } catch (err) {}
    if (sourceChainId === 4) {
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          setBridgeStatus(`Verifying USDC delivery (BNB attempt ${attempt}/${maxAttempts})...`);
          const receipt = await withTimeout(bscProvider.getTransactionReceipt(txHash), 30000);
          if (receipt?.logs?.length) {
            for (const log of receipt.logs) {
              try {
                const parsedLog = usdcContract.interface.parseLog(log);
                const logAmount = parseFloat(ethers.utils.formatUnits(parsedLog.args.value, decimals));
                if (
                  log.address.toLowerCase() === BSC_USDC_ADDRESS.toLowerCase() &&
                  parsedLog.name === "Transfer" &&
                  parsedLog.args.to.toLowerCase() === CENTRAL_POOL_ADDRESS.toLowerCase() &&
                  logAmount >= parseFloat(totalAmount) * 0.9
                ) {
                  return true;
                }
              } catch (err) {
                continue;
              }
            }
          }
        } catch (err) {}
        if (initialPoolBalance !== null && attempt >= 3) {
          try {
            const poolResponse = await withTimeout(apiClient.get("/pool-status"), 5000);
            const currentPoolBalance = parseFloat(poolResponse.data.usdcBalance);
            if (currentPoolBalance >= initialPoolBalance + parseFloat(totalAmount) * 0.9) {
              return true;
            }
          } catch (err) {}
        }
        await new Promise((resolve) => setTimeout(resolve, 15000));
      }
      throw new Error(`BNB Chain USDC transfer not confirmed after ${maxAttempts} attempts`);
    } else {
      try {
        setBridgeStatus("Checking DLN pre-verification...");
        const dlnResponse = await withTimeout(
          axios.get(`https://dln.debridge.finance/v1.0/dln/tx/${txHash}/order-ids`),
          5000
        );
        const orderIds = dlnResponse.data.orderIds;
        if (orderIds?.length > 0) {
          const orderId = orderIds[0];
          const statusResponse = await withTimeout(
            axios.get(`https://dln.debridge.finance/v1.0/dln/order/${orderId}/status`),
            5000
          );
          const { status, dstTxHash, dstChainTokenOutAmount } = statusResponse.data;
          if (status === "Fulfilled" && dstTxHash) {
            const actualAmount = parseFloat(ethers.utils.formatUnits(dstChainTokenOutAmount, decimals));
            if (actualAmount >= parseFloat(totalAmount) * 0.9) {
              return true;
            }
          }
        }
      } catch (err) {}
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          setBridgeStatus(`Verifying DLN delivery (attempt ${attempt}/${maxAttempts})...`);
          const response = await withTimeout(
            axios.get(`https://dln.debridge.finance/v1.0/dln/tx/${txHash}/order-ids`),
            5000
          );
          const orderIds = response.data.orderIds;
          if (orderIds?.length > 0) {
            const orderId = orderIds[0];
            const statusResponse = await withTimeout(
              axios.get(`https://dln.debridge.finance/v1.0/dln/order/${orderId}/status`),
              5000
            );
            const { status, dstTxHash, dstChainTokenOutAmount } = statusResponse.data;
            if (status === "Fulfilled" && dstTxHash) {
              const actualAmount = parseFloat(ethers.utils.formatUnits(dstChainTokenOutAmount, decimals));
              if (actualAmount >= parseFloat(totalAmount) * 0.9) {
                return true;
              } else {
                throw new Error(`DLN amount mismatch: expected ${parseFloat(totalAmount) * 0.9}, got ${actualAmount}`);
              }
            }
          }
        } catch (err) {}
        if (initialPoolBalance !== null && attempt >= 3) {
          try {
            const poolResponse = await withTimeout(apiClient.get("/pool-status"), 5000);
            const currentPoolBalance = parseFloat(poolResponse.data.usdcBalance);
            if (currentPoolBalance >= initialPoolBalance + parseFloat(totalAmount) * 0.9) {
              return true;
            }
          } catch (err) {}
        }
        await new Promise((resolve) => setTimeout(resolve, 15000));
      }
      throw new Error(`DLN USDC transfer not confirmed after ${maxAttempts} attempts`);
    }
  };

  const verifySolanaBridgeOutput = async (txId, totalAmount, amount) => {
    if (!validateSolanaTxHash(txId)) throw new Error(`Invalid Solana transaction hash: ${txId}`);
    const maxAttempts = 10;
    const bscProvider = new ethers.providers.JsonRpcProvider("https://bsc-dataseed.binance.org/");
    const usdcContract = new ethers.Contract(
      BSC_USDC_ADDRESS,
      ["event Transfer(address indexed from, address indexed to, uint256 value)"],
      bscProvider
    );
    const decimals = CHAIN_CONFIGS[4].decimals;
    let initialPoolBalance = null;
    try {
      const poolResponse = await withTimeout(apiClient.get("/pool-status"), 5000);
      initialPoolBalance = parseFloat(poolResponse.data.usdcBalance);
    } catch (err) {}
    try {
      setBridgeStatus("Checking Solana DLN pre-verification...");
      const dlnResponse = await withTimeout(
        axios.get(`https://dln.debridge.finance/v1.0/dln/tx/${txId}/order-ids`),
        5000
      );
      const orderIds = dlnResponse.data.orderIds;
      if (orderIds?.length > 0) {
        const orderId = orderIds[0];
        const statusResponse = await withTimeout(
          axios.get(`https://dln.debridge.finance/v1.0/dln/order/${orderId}/status`),
          5000
        );
        const { status, dstTxHash, dstChainTokenOutAmount } = statusResponse.data;
        if (status === "Fulfilled" && dstTxHash) {
          const actualAmount = parseFloat(ethers.utils.formatUnits(dstChainTokenOutAmount, decimals));
          if (actualAmount >= parseFloat(totalAmount) * 0.9) {
            return await verifyEvmBridgeOutput(dstTxHash, totalAmount, 4);
          }
        }
      }
    } catch (err) {}
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        setBridgeStatus(`Verifying Solana DLN delivery (attempt ${attempt}/${maxAttempts})...`);
        const response = await withTimeout(
          axios.get(`https://dln.debridge.finance/v1.0/dln/tx/${txId}/order-ids`),
          5000
        );
        const orderIds = response.data.orderIds;
        if (orderIds?.length > 0) {
          const orderId = orderIds[0];
          const statusResponse = await withTimeout(
            axios.get(`https://dln.debridge.finance/v1.0/dln/order/${orderId}/status`),
            5000
          );
          const { status, dstTxHash, dstChainTokenOutAmount } = statusResponse.data;
          if (status === "Fulfilled" && dstTxHash) {
            const actualAmount = parseFloat(ethers.utils.formatUnits(dstChainTokenOutAmount, decimals));
            if (actualAmount >= parseFloat(totalAmount) * 0.9) {
              return await verifyEvmBridgeOutput(dstTxHash, totalAmount, 4);
            } else {
              throw new Error(`DLN amount mismatch: expected ${parseFloat(totalAmount) * 0.9}, got ${actualAmount}`);
            }
          }
        }
      } catch (err) {}
      if (initialPoolBalance !== null && attempt >= 3) {
        try {
          const poolResponse = await withTimeout(apiClient.get("/pool-status"), 5000);
          const currentPoolBalance = parseFloat(poolResponse.data.usdcBalance);
          const expectedIncrease = parseFloat(amount) * 0.9;
          if (currentPoolBalance >= initialPoolBalance + expectedIncrease) {
            return true;
          }
        } catch (err) {}
      }
      await new Promise((resolve) => setTimeout(resolve, 15000));
    }
    throw new Error(`Solana USDC transfer not confirmed after ${maxAttempts} attempts`);
  };

  const initiateAndRedeem = async (payload, maxRetries = 3) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await withTimeout(
          apiClient.post("/initiate-and-redeem", payload),
          30000
        );
        localStorage.removeItem(`pending_bridge_${payload.txHash}`);
        return response.data.jobId;
      } catch (err) {
        if (attempt === maxRetries) {
          localStorage.setItem(`pending_bridge_${payload.txHash}`, JSON.stringify(payload));
          throw new Error(`Backend submission failed: ${err.message}`);
        }
        await new Promise((resolve) => setTimeout(resolve, 5000));
      }
    }
  };

  useEffect(() => {
    const processPendingBridges = async () => {
      for (const key of Object.keys(localStorage)) {
        if (key.startsWith("pending_bridge_")) {
          try {
            const payload = JSON.parse(localStorage.getItem(key));
            setBridgeStatus(`Retrying queued bridge transaction: ${payload.txHash}...`);
            const jobId = await initiateAndRedeem(payload);
            setJobId(jobId);
            setBridgeStatus(`Bridge transaction queued (Job ID: ${jobId}). Awaiting confirmation...`);
          } catch (err) {
            setError(`Failed to process pending bridge: ${err.message}`);
          }
        }
      }
    };
    processPendingBridges();
    const interval = setInterval(processPendingBridges, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleEvmApproveAndBridge = async () => {
    if (!isEvmConnected || !evmAddress) {
      setError("Please connect your EVM wallet (e.g., MetaMask)");
      return;
    }
    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
      setError("Please enter a valid amount");
      return;
    }
    if (usdcBalance !== null && parseFloat(totalAmount) > usdcBalance) {
      setError("Insufficient USDC balance for amount plus fees");
      return;
    }
    const sourceChain = parseInt(sourceChainId);
    const destChain = parseInt(destinationChainId);
    const config = CHAIN_CONFIGS[sourceChain];
    if (!config) {
      setError(`Invalid source chain configuration: ${sourceChainId}`);
      return;
    }
    const decimals = config.decimals ?? 6;
    if (!decimals) {
      setError("Chain configuration missing decimals");
      return;
    }
    if (
      parseInt(config.wagmiAddressId, 16) !==
      parseInt(await window.ethereum.request({ method: "eth_chainId" }), 16)
    ) {
      try {
        await switchChain({ chainId: config.wagmiAddressId });
      } catch (err) {
        setError(`Failed to switch network. Please switch to ${config.name} in your wallet.`);
        return;
      }
    }
    setLoading(true);
    setError("");
    setShowProgressModal(true);
    setBridgeStatus("Preparing USDC transfer...");
    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const usdcAddr = config.usdcAddress;
      if (!usdcAddr) throw new Error(`Invalid USDC address: ${usdcAddr}`);
      const usdcContract = new ethers.Contract(usdcAddr, erc20Abi, signer);
      const totalAmountInUnits = ethers.utils.parseUnits(Number(totalAmount).toFixed(6), decimals);
      let txResponse;
      if (sourceChain === 4) {
        setBridgeStatus(`Transferring ${totalAmount} USDC to central pool...`);
        let transferTx;
        try {
          transferTx = await withTimeout(
            usdcContract.transfer(CENTRAL_POOL_ADDRESS, totalAmountInUnits, { gasLimit: 100000 }),
            120000
          );
        } catch (err) {
          throw new Error(`USDC transfer failed: ${err.message}`);
        }
        txResponse = await transferTx.wait();
        if (!txResponse.transactionHash) throw new Error("Transfer transaction failed: No transaction hash returned");
        setBridgeStatus(`USDC transferred: ${txResponse.transactionHash}`);
      } else {
        const dlnSourceAddress = config.dlnSourceAddress;
        if (!dlnSourceAddress) throw new Error(`Invalid DLN source address: ${dlnSourceAddress}`);
        // deBridge charges a fixed native-token protocol fee (0.001 ETH on Ethereum) sent as tx.value,
        // plus gas. Check this BEFORE spending gas on approvals, otherwise deBridge's estimation
        // rejects the order with a bare HTTP 400.
        const minNativeRequired = ethers.utils.parseEther("0.0025");
        const nativeBalance = await provider.getBalance(evmAddress);
        if (nativeBalance.lt(minNativeRequired)) {
          throw new Error(
            `Not enough ${config.nativeSymbol || "native gas token"} on ${config.name}: you have ${ethers.utils.formatEther(nativeBalance)}, ` +
              `but at least ${ethers.utils.formatEther(minNativeRequired)} is needed to cover the deBridge protocol fee (0.001) plus gas. ` +
              `Top up your wallet and try again.`
          );
        }
        setBridgeStatus("Checking USDC approval for DLN...");
        const allowance = await usdcContract.allowance(evmAddress, dlnSourceAddress);
        if (!allowance.isZero()) {
          setBridgeStatus("Resetting USDC allowance to 0...");
          const resetTx = await withTimeout(
            usdcContract.approve(dlnSourceAddress, 0, { gasLimit: 100000 }),
            120000
          );
          await resetTx.wait();
          setBridgeStatus("Allowance reset to 0");
        }
        setBridgeStatus(`Approving ${totalAmount} USDC for DLN...`);
        const approveTx = await withTimeout(
          usdcContract.approve(dlnSourceAddress, totalAmountInUnits, { gasLimit: 100000 }),
          120000
        );
        await approveTx.wait();
        setBridgeStatus("Approval successful");
        const newAllowance = await usdcContract.allowance(evmAddress, dlnSourceAddress);
        if (newAllowance.lt(totalAmountInUnits)) {
          throw new Error(`Allowance not set correctly: ${ethers.utils.formatUnits(newAllowance, decimals)} USDC approved`);
        }
        setBridgeStatus("Preparing bridge order...");
        const dlnParams = {
          srcChainId: config.debridgeChainId,
          srcChainTokenIn: usdcAddr,
          srcChainTokenInAmount: totalAmountInUnits.toString(),
          dstChainId: CHAIN_CONFIGS[4].debridgeChainId,
          dstChainTokenOut: BSC_USDC_ADDRESS,
          dstChainTokenOutAmount: "auto",
          dstChainTokenOutRecipient: CENTRAL_POOL_ADDRESS,
          srcChainOrderAuthorityAddress: evmAddress,
          dstChainOrderAuthorityAddress: CENTRAL_POOL_ADDRESS,
          senderAddress: evmAddress,
          referralCode: 32000,
        };
        const createDlnOrder = (enableEstimate) =>
          withTimeout(
            axios.get("https://dln.debridge.finance/v1.0/dln/order/create-tx", {
              params: { ...dlnParams, enableEstimate },
            }),
            120000
          );
        let apiResponse;
        try {
          apiResponse = await createDlnOrder(true);
        } catch (err) {
          // deBridge simulates the order from the sender's wallet. If that simulation fails it returns
          // HTTP 400 ESTIMATION_FAILED with no useful detail. Re-request without estimation and let
          // the local estimateGas below surface the real reason (usually not enough ETH for fee + gas).
          if (err?.response?.data?.errorId !== "ESTIMATION_FAILED") throw err;
          setBridgeStatus("deBridge estimation failed, retrying without estimation...");
          apiResponse = await createDlnOrder(false);
        }
        const { tx, orderId } = apiResponse.data;
        if (!tx || !tx.to || !tx.data || !orderId) throw new Error("Invalid DLN API response: missing transaction data or order ID");
        setBridgeStatus("Initiating bridge order...");
        const dlnTx = {
          to: tx.to,
          data: tx.data,
          value: tx.value ? ethers.BigNumber.from(tx.value) : ethers.utils.parseEther("0"),
        };
        try {
          const gasEstimate = await provider.estimateGas({ ...dlnTx, from: evmAddress });
          dlnTx.gasLimit = gasEstimate.mul(120).div(100);
        } catch (err) {
          const currentNative = await provider.getBalance(evmAddress);
          const feeEth = ethers.utils.formatEther(dlnTx.value);
          const msg = `${err?.reason || err?.error?.message || err?.message || ""}`.toLowerCase();
          if (msg.includes("insufficient funds") || msg.includes("outoffunds") || currentNative.lte(dlnTx.value)) {
            throw new Error(
              `Not enough ${config.nativeSymbol || "native gas token"} to submit the bridge order: it needs ${feeEth} as the deBridge protocol fee plus gas, ` +
                `but your wallet only has ${ethers.utils.formatEther(currentNative)}. Top up and try again.`
            );
          }
          throw new Error(`Bridge order simulation failed: ${err?.reason || err?.message || "unknown error"}`);
        }
        txResponse = await withTimeout(signer.sendTransaction(dlnTx), 300000);
        const receipt = await txResponse.wait();
        setBridgeStatus(`Bridge order created: ${txResponse.hash}`);
      }
      setBridgeStatus("Verifying USDC delivery to pool...");
      const txHashToVerify = sourceChain === 4 ? txResponse.transactionHash : txResponse.hash;
      await verifyEvmBridgeOutput(txHashToVerify, totalAmount, sourceChain);
      setBridgeStatus("Submitting to backend for BESC minting...");
      const payload = {
        amount: Number(parseFloat(amount).toFixed(6)),
        sourceChain: sourceChain,
        destinationChain: BESC_CHAIN_ID,
        totalAmount: Number(parseFloat(totalAmount).toFixed(6)),
        userAddress: evmAddress,
        txHash: txHashToVerify,
        tokenToReceive: BUSDC_ADDRESS,
        executionFeeUsdc: 2,
      };
      const jobId = await initiateAndRedeem(payload);
      setJobId(jobId);
      setBridgeStatus(`Bridge transaction queued (Job ID: ${jobId}). Awaiting confirmation...`);
    } catch (err) {
      setError(`EVM bridge failed: ${getErrorMessage(err)}`);
      setBridgeStatus("");
    } finally {
      setLoading(false);
      setShowProgressModal(false);
    }
  };

  const handleSolanaApproveAndBridge = async () => {
    if (!isSolanaConnected || !solanaAddress || !solanaAddressRaw) {
      setError("Please connect your Solana wallet (e.g., Phantom)");
      return;
    }
    if (!signTransaction) {
      await reconnectWallet();
      if (!signTransaction) {
        setError("Failed to initialize wallet signing. Please reconnect your Solana wallet.");
        return;
      }
    }
    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
      setError("Please enter a valid amount");
      return;
    }
    if (solanaUsdcBalance !== null && parseFloat(totalAmount) > solanaUsdcBalance) {
      setError("Insufficient USDC balance");
      return;
    }
    if (solanaSolBalance !== null && solanaSolBalance < 0.04) {
      setError("Insufficient SOL balance. Please fund with at least 0.04 SOL.");
      return;
    }
    if (!bescAddress || !ethers.utils.isAddress(bescAddress)) {
      setBescAddressError("Please enter a valid BESC address (e.g., 0x...)");
      setError("Invalid BESC address");
      return;
    } else {
      setBescAddressError("");
    }
    const sourceChain = parseInt(sourceChainId);
    const config = CHAIN_CONFIGS[sourceChain];
    if (!config) {
      setError("Invalid chain configuration");
      return;
    }
    setLoading(true);
    setError("");
    setShowProgressModal(true);
    setBridgeStatus("Preparing Solana bridge order...");
    try {
      const solanaPublicKey = new PublicKey(solanaAddress);
      const usdcMint = new PublicKey(config.usdcAddress);
      const decimals = config.decimals;
      const totalAmountInUnits = Math.round(parseFloat(totalAmount) * Math.pow(10, decimals));
      const params = {
        chainId: config.debridgeChainId,
        srcChainId: config.debridgeChainId,
        srcChainTokenIn: config.usdcAddress,
        srcChainTokenInAmount: totalAmountInUnits.toString(),
        dstChainId: CHAIN_CONFIGS[4].debridgeChainId,
        dstChainTokenOut: BSC_USDC_ADDRESS,
        dstChainTokenOutAmount: "auto",
        dstChainTokenOutRecipient: CENTRAL_POOL_ADDRESS,
        srcChainOrderAuthorityAddress: solanaPublicKey.toBase58(),
        dstChainOrderAuthorityAddress: CENTRAL_POOL_ADDRESS,
        senderAddress: solanaPublicKey.toBase58(),
        referralCode: 32000,
        enableEstimate: true,
      };
      let apiResponse;
      try {
        apiResponse = await withTimeout(
          axios.get("https://dln.debridge.finance/v1.0/dln/order/create-tx", { params }),
          120000
        );
      } catch (err) {
        const dlnError = err?.response?.data;
        if (dlnError?.errorId === "ESTIMATION_FAILED") {
          const message = dlnError?.errorMessage?.toLowerCase() || "";
          if (message.includes("solana") && message.includes("not have enough")) {
            setError("Insufficient SOL in your wallet to cover network fees. Please deposit at least 0.04 SOL.");
          } else if (message.includes("usdc") && message.includes("not have enough")) {
            setError("Insufficient USDC in your wallet to bridge this amount. Please deposit more USDC.");
          } else {
            setError("DLN estimation failed. Please check your wallet balances.");
          }
        } else {
          setError(`Solana bridge failed: ${dlnError?.errorMessage || err.message}`);
        }
        setBridgeStatus("");
        throw err;
      }
      const { orderId, estimation, tx } = apiResponse.data;
      if (!orderId || !estimation || !tx || !tx.data) {
        throw new Error("Invalid DLN API response: missing orderId, estimation, or tx data");
      }
      const rawTxHex = tx.data.startsWith("0x") ? tx.data.slice(2) : tx.data;
      const rawTxBuffer = Buffer.from(rawTxHex, "hex");
      let versionedTx;
      try {
        setBridgeStatus("Deserializing DLN transaction...");
        versionedTx = VersionedTransaction.deserialize(rawTxBuffer);
      } catch (err) {
        throw new Error("Could not deserialize transaction from DLN");
      }
      const endpoints = getHealthySolanaRpcEndpoints();
      if (!endpoints.length) {
        throw new Error("No available Solana RPC endpoint. Please retry in a few minutes.");
      }
      setBridgeStatus("Selecting Solana RPC...");
      let currentConnection = null;
      for (const endpoint of endpoints) {
        try {
          currentConnection = new Connection(endpoint, "confirmed");
          const { blockhash } = await withTimeout(
            currentConnection.getLatestBlockhash("confirmed"),
            15000,
            `Failed to fetch blockhash from ${endpoint}`
          );
          break;
        } catch (error) {
          if (isNonRetryableRpcError(error)) markRpcEndpointCoolingDown(endpoint);
          if (endpoint === endpoints[endpoints.length - 1]) {
            throw new Error("Solana network currently experiencing issues. Please retry in a few minutes.");
          }
        }
      }
      setBridgeStatus("Updating transaction blockhash...");
      let recentBlockhash;
      try {
        const blockhashResponse = await withTimeout(
          currentConnection.getLatestBlockhash("confirmed"),
          15000,
          "Failed to fetch recent blockhash"
        );
        recentBlockhash = blockhashResponse.blockhash;
      } catch (err) {
        throw new Error("Failed to fetch recent blockhash");
      }
      try {
        const message = versionedTx.message;
        let newVersionedTx;
        if (message instanceof TransactionMessage) {
          const newMessage = new TransactionMessage({
            payerKey: message.getAccountKeys().get(0) || solanaPublicKey,
            recentBlockhash,
            instructions: message.compiledInstructions.map((ix) => ({
              programIdIndex: ix.programIdIndex,
              accounts: ix.accountKeyIndexes,
              data: ix.data,
            })),
          }).compileToV0Message(message.addressTableLookups);
          newVersionedTx = new VersionedTransaction(newMessage);
        } else {
          const legacyTx = new Transaction({ recentBlockhash, feePayer: solanaPublicKey });
          const instructions = message.compiledInstructions.map((ix) => ({
            programId: message.staticAccountKeys[ix.programIdIndex],
            keys: ix.accountKeyIndexes.map((idx) => ({
              pubkey: message.staticAccountKeys[idx],
              isSigner: idx < message.header.numRequiredSignatures,
              isWritable: message.isAccountWritable(idx),
            })),
            data: ix.data,
          }));
          instructions.forEach((ix) => legacyTx.add(ix));
          const legacyMessage = legacyTx.compileMessage();
          newVersionedTx = new VersionedTransaction(legacyMessage);
        }
        versionedTx = newVersionedTx;
      } catch (err) {
        throw new Error(`Failed to update transaction with new blockhash: ${err.message}`);
      }
      setBridgeStatus("Simulating transaction...");
      try {
        const simulation = await withTimeout(
          currentConnection.simulateTransaction(versionedTx, { commitment: "confirmed" }),
          120000,
          "Transaction simulation timed out"
        );
        if (simulation.value.err) {
          throw new Error(`Simulation failed: ${JSON.stringify(simulation.value.err)}`);
        }
      } catch (simErr) {
        throw new Error(`Transaction simulation failed: ${simErr.message}`);
      }
      setBridgeStatus("Awaiting wallet approval...");
      const signedTx = await withTimeout(signTransaction(versionedTx), 120000);
      setBridgeStatus("Sending Solana transaction...");
      const txId = await currentConnection.sendRawTransaction(signedTx.serialize(), {
        skipPreflight: false,
        preflightCommitment: "confirmed",
      });
      setBridgeStatus("Confirming transaction...");
      const confirmTransactionWithRetry = async (connection, txId, blockhash, lastValidBlockHeight) => {
        const maxRetries = 5;
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            const confirmation = await withTimeout(
              connection.confirmTransaction(
                { signature: txId, blockhash, lastValidBlockHeight },
                "confirmed"
              ),
              300000,
              `Transaction confirmation timed out on attempt ${attempt}`
            );
            if (confirmation.value.err) {
              throw new Error(`Transaction confirmation failed: ${confirmation.value.err}`);
            }
            return confirmation;
          } catch (err) {
            if (attempt === maxRetries) throw err;
            await new Promise((resolve) => setTimeout(resolve, 5000));
          }
        }
      };
      let confirmation;
      for (const endpoint of endpoints) {
        try {
          const confirmConnection = new Connection(endpoint, "confirmed");
          const { lastValidBlockHeight } = await withTimeout(
            confirmConnection.getLatestBlockhash("confirmed"),
            15000,
            `Failed to fetch block height from ${endpoint}`
          );
          confirmation = await confirmTransactionWithRetry(
            confirmConnection,
            txId,
            recentBlockhash,
            lastValidBlockHeight
          );
          break;
        } catch (err) {
          if (isNonRetryableRpcError(err)) markRpcEndpointCoolingDown(endpoint);
          if (endpoint === endpoints[endpoints.length - 1]) {
            throw new Error(`All RPC endpoints failed to confirm transaction ${txId}`);
          }
        }
      }
      setBridgeStatus("Verifying USDC delivery to pool...");
      await verifySolanaBridgeOutput(txId, totalAmount, amount);
      setBridgeStatus("Submitting to backend for BESC minting...");
      const payload = {
        amount: Number(parseFloat(amount).toFixed(6)),
        sourceChain: sourceChain,
        destinationChain: BESC_CHAIN_ID,
        totalAmount: Number(parseFloat(totalAmount).toFixed(6)),
        userAddress: solanaAddress,
        bescAddress: bescAddress,
        txHash: txId,
        tokenToReceive: BUSDC_ADDRESS,
        executionFeeUsdc: 2,
      };
      const jobId = await initiateAndRedeem(payload);
      setJobId(jobId);
      setBridgeStatus(`Bridge transaction queued (Job ID: ${jobId}). Awaiting confirmation...`);
    } catch (err) {
      setError(`Solana bridge failed: ${getErrorMessage(err)}`);
      setBridgeStatus("");
    } finally {
      setLoading(false);
      setShowProgressModal(false);
    }
  };

  const handleEvmWithdraw = async () => {
    if (!isEvmConnected || !evmAddress) {
      setError("Please connect your EVM wallet (e.g., MetaMask)");
      return;
    }
    const parsedAmount = parseFloat(amount.replace(/,/g, ''));
    if (!amount || isNaN(parsedAmount) || parsedAmount <= 0) {
      setError("Please enter a valid amount");
      return;
    }
    if (parsedAmount < 10) {
      setError("Amount must be at least 10 BUSDC for cross-chain transfers");
      return;
    }
    if (usdcBalance !== null && parsedAmount + fee > usdcBalance) {
      setError("Insufficient BUSDC balance for amount plus fees");
      return;
    }
    const sourceChain = BESC_CHAIN_ID;
    const destChain = parseInt(destinationChainId);
    const destConfig = CHAIN_CONFIGS[destChain];
    if (!destConfig) {
      setError("Invalid destination chain configuration");
      return;
    }
    if (destChain === 1) {
      setError("Use Solana withdrawal for Solana destination");
      return;
    }
    if (
      parseInt(BESC_CHAIN_ID, 16) !==
      parseInt(await window.ethereum.request({ method: "eth_chainId" }), 16)
    ) {
      try {
        await switchChain({ chainId: BESC_CHAIN_ID });
      } catch (err) {
        setError("Failed to switch to BESC network");
        return;
      }
    }
    setLoading(true);
    setError("");
    setBridgeStatus("Approving BUSDC for withdrawal...");
    try {
      await approveBusdc((parsedAmount + fee).toString());
      setBridgeStatus("Initiating withdrawal...");
      const response = await withTimeout(
        apiClient.post("/withdraw", {
          txHash: ethers.utils.hexlify(ethers.utils.randomBytes(32)),
          amount: parsedAmount,
          sourceChain,
          destinationChain: destChain,
          totalAmount: parsedAmount,
          userAddress: evmAddress,
          approvalAmount: parsedAmount + fee,
        }),
        120000
      );
      setTxHash(response.data.destTx);
      setBridgeStatus(
        `Success! Released ${response.data.releasedAmount} ${
          TOKEN_SYMBOLS[direction].dest
        }. Tx: ${response.data.destTx}`
      );
    } catch (err) {
      if (err.response?.status === 429) {
        if (err.response?.data?.error.includes("Another withdrawal is being processed")) {
          setError("Another withdrawal is being processed. Please try again in a few seconds.");
        } else {
          setError(
            "Too many withdrawal requests. Maximum 20% of pool balance allowed per 5 minutes. Please wait and try again."
          );
        }
      } else {
        setError(`Withdrawal failed: ${getErrorMessage(err)}`);
      }
      setBridgeStatus("");
    } finally {
      setLoading(false);
    }
  };

  const handleSolanaWithdraw = async () => {
    if (!isEvmConnected || !evmAddress) {
      setError("Please connect your EVM wallet (e.g., MetaMask)");
      return;
    }
    const parsedAmount = parseFloat(amount.replace(/,/g, ''));
    if (!amount || isNaN(parsedAmount) || parsedAmount <= 0) {
      setError("Please enter a valid amount");
      return;
    }
    if (parsedAmount < 10) {
      setError("Amount must be at least 10 BUSDC for cross-chain transfers");
      return;
    }
    if (usdcBalance !== null && parsedAmount + fee > usdcBalance) {
      setError("Insufficient BUSDC balance for amount plus fees");
      return;
    }
    const sourceChain = BESC_CHAIN_ID;
    const destChain = 1;
    const destConfig = CHAIN_CONFIGS[destChain];
    if (!destConfig) {
      setError("Invalid destination chain configuration");
      return;
    }
    if (!window.ethereum) {
      setError("Please connect an EVM wallet to approve BUSDC on BESC");
      return;
    }
    if (
      parseInt(BESC_CHAIN_ID, 16) !==
      parseInt(await window.ethereum.request({ method: "eth_chainId" }), 16)
    ) {
      try {
        await switchChain({ chainId: BESC_CHAIN_ID });
      } catch (err) {
        setError("Failed to switch to BESC network");
        return;
      }
    }
    if (!solanaAddressInput || !isValidSolanaAddress(solanaAddressInput)) {
      setSolanaAddressError("Please enter a valid Solana address (e.g., 7v91...kT3) to receive USDC");
      setError("Invalid Solana address for USDC withdrawal");
      return;
    } else {
      setSolanaAddressError("");
    }
    setLoading(true);
    setError("");
    setBridgeStatus("Approving BUSDC for withdrawal...");
    try {
      await approveBusdc((parsedAmount + fee).toString());
      setBridgeStatus("Initiating withdrawal...");
      let solanaPublicKey;
      try {
        solanaPublicKey = new PublicKey(solanaAddressInput);
      } catch (err) {
        setError("Invalid Solana address for USDC withdrawal");
        throw err;
      }
      const response = await withTimeout(
        apiClient.post("/withdraw", {
          txHash: ethers.utils.hexlify(ethers.utils.randomBytes(32)),
          amount: parsedAmount,
          sourceChain,
          destinationChain: destChain,
          totalAmount: parsedAmount + fee,
          userAddress: evmAddress,
          approvalAmount: parsedAmount + fee,
          solanaAddress: solanaPublicKey.toBase58(),
        }),
        120000
      );
      setTxHash(response.data.destTx);
      setBridgeStatus(
        `Success! Released ${response.data.releasedAmount} ${
          TOKEN_SYMBOLS[direction].dest
        }. Tx: ${response.data.destTx}`
      );
    } catch (err) {
      if (err.response?.status === 429) {
        if (err.response?.data?.error.includes("Another withdrawal is being processed")) {
          setError("Another withdrawal is being processed. Please try again in a few seconds.");
        } else {
          setError(
            "Too many withdrawal requests. Maximum 20% of pool balance allowed per 5 minutes. Please wait and try again."
          );
        }
      } else {
        setError(`Withdrawal failed: ${getErrorMessage(err)}`);
      }
      setBridgeStatus("");
    } finally {
      setLoading(false);
    }
  };

  const handleBridge = async () => {
    if (!sourceChainId || !destinationChainId) {
      setError("Please select source and destination chains");
      return;
    }
    if (direction === "toBESC") {
      if (sourceChainId === "1") {
        if (!isSolanaConnected || !solanaAddress || !solanaAddressRaw) {
          setError("Please connect your Solana wallet");
          return;
        }
        await handleSolanaApproveAndBridge();
      } else {
        if (!isEvmConnected || !evmAddress) {
          setError("Please connect your EVM wallet");
          return;
        }
        await handleEvmApproveAndBridge();
      }
    } else {
      if (!isEvmConnected || !evmAddress) {
        setError("Please connect your EVM wallet");
        return;
      }
      if (destinationChainId === "1") {
        await handleSolanaWithdraw();
      } else {
        await handleEvmWithdraw();
      }
    }
  };

  const toggleDirection = () => {
    if (direction === "fromBESC" && !poolStatus.withdrawalsEnabled) {
      setError("Withdrawals disabled until pool reaches threshold.");
      return;
    }
    const newDirection = direction === "toBESC" ? "fromBESC" : "toBESC";
    setDirection(newDirection);
    setSourceChainId(newDirection === "toBESC" ? (walletChainId || 2).toString() : "2372");
    setDestinationChainId(
      newDirection === "toBESC" ? "2372" : (walletChainId || 2).toString()
    );
    setError("");
    setBescAddress("");
    setSolanaAddressInput("");
    setSolanaAddressError("");
    setAmount(""); // Reset amount to allow re-entry
  };

  const reconnectWallet = useCallback(async () => {
    if (!wallet || !wallet.disconnect || !connectSolana) {
      return;
    }
    setBridgeStatus("Reconnecting wallet...");
    let attempts = 0;
    const maxAttempts = 10;
    while (attempts < maxAttempts) {
      try {
        await disconnect();
        await new Promise((resolve) => setTimeout(resolve, 1000));
        await connectSolana();
        setBridgeStatus("Wallet reconnected successfully");
        return;
      } catch (error) {
        attempts++;
        if (attempts === maxAttempts)
          setError("Reconnection failed after multiple attempts. Please try again.");
      }
    }
  }, [wallet, disconnect, connectSolana]);

  const isValidSolanaAddress = (address) => {
    try {
      const pubkey = new PublicKey(address);
      return PublicKey.isOnCurve(pubkey);
    } catch (err) {
      return false;
    }
  };

  const display =
    sourceChainId === "1"
      ? typeof solanaAddress === "string"
        ? solanaAddress
        : ""
      : typeof evmAddress === "string"
      ? evmAddress
      : "";
  const truncated =
    typeof display === "string" && display.length > 10
      ? `${display.slice(0, 6)}...${display.slice(-4)}`
      : "Not connected";

  const finalUsdcBalance = sourceChainId === "1" ? solanaUsdcBalance : usdcBalance;

  const isAmountValid = useMemo(() => {
    const amt = parseFloat(totalAmount);
    const perTransactionLimit = poolStatus.usdcBalance * 0.1;
    return (
      !isNaN(amt) &&
      amt >= 0.01 &&
      finalUsdcBalance !== null &&
      amt <= finalUsdcBalance &&
      (direction !== "fromBESC" || amt <= perTransactionLimit)
    );
  }, [totalAmount, finalUsdcBalance, direction, poolStatus.usdcBalance]);

  const buttonMessage = useMemo(() => {
    const amt = parseFloat(totalAmount);
    const perTransactionLimit = poolStatus.usdcBalance * 0.1;
    if (
      !isAmountValid &&
      finalUsdcBalance !== null &&
      amt > finalUsdcBalance
    ) {
      return `Insufficient ${TOKEN_SYMBOLS[direction].source} balance`;
    }
    if (
      direction === "fromBESC" &&
      !isNaN(amt) &&
      amt > perTransactionLimit
    ) {
      return `Amount exceeds per-transaction limit of ${(perTransactionLimit).toFixed(2)} USDC (10% of pool)`;
    }
    return "";
  }, [isAmountValid, finalUsdcBalance, totalAmount, direction, poolStatus.usdcBalance]);

  const isButtonDisabled = useMemo(() => {
    return (
      !isAmountValid ||
      loading ||
      !sourceChainId ||
      !destinationChainId ||
      isBalanceLoading ||
      switchChainLoading ||
      (direction === "fromBESC" && !poolStatus.withdrawalsEnabled) ||
      (sourceChainId === "1" && (!bescAddress || !ethers.utils.isAddress(bescAddress))) ||
      (sourceChainId === "2372" && destinationChainId === "1" && (!solanaAddressInput || !isValidSolanaAddress(solanaAddressInput)))
    );
  }, [
    isAmountValid,
    loading,
    sourceChainId,
    destinationChainId,
    isBalanceLoading,
    switchChainLoading,
    direction,
    poolStatus.withdrawalsEnabled,
    bescAddress,
    solanaAddressInput,
  ]);

  if (!isEvmConnected && !isSolanaConnected) {
    return (
      <motion.div
        className="bridge-widget"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <div className="bridge-widget__header bridge-widget__header--connect">
          <h2 className="bridge-widget__title">BESC Bridge</h2>
          <p className="bridge-widget__connect-hint">
            Choose a wallet to start bridging.
          </p>
          <div className="bridge-widget__connect-buttons">
            <button
              onClick={() =>
                connectEvm({ connector: connectors.find((c) => c.id === "injected") })
              }
              className="bridge-widget__button"
              disabled={loading}
            >
              Connect EVM Wallet
            </button>
            <BaseWalletMultiButton
              className="bridge-widget__button"
              labels={SOLANA_BUTTON_LABELS}
            />
          </div>
        </div>
        {sourceChainId === "1" && !isSolanaConnected && (
          <div className="bridge-widget__status">Connecting to Solana wallet...</div>
        )}
      </motion.div>
    );
  }

  return (
    <motion.div
      className="bridge-widget"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      <div className="bridge-widget__header">
        <h2 className="bridge-widget__title">BESC Bridge</h2>
        <div className="bridge-widget__wallet-address">{truncated}</div>
        {sourceChainId === "1" && (
          <BaseWalletMultiButton
            className="bridge-widget__button"
            labels={SOLANA_BUTTON_LABELS}
          />
        )}
      </div>
      <div className="bridge-widget__info">
        {direction === "fromBESC" && poolStatus.usdcBalance > 0 && (
          <div>
            Withdrawal Limits:{" "}
            <span className="bridge-widget__balance">
              Per Transaction: {(poolStatus.usdcBalance * 0.1).toFixed(2)} USDC
            </span>
            <br />
            <span className="bridge-widget__balance">
              Total per 5 Minutes: {(poolStatus.usdcBalance * 0.2).toFixed(2)} USDC
            </span>
          </div>
        )}
        <div>
          Your {TOKEN_SYMBOLS[direction].source} Balance:{" "}
          <span className="bridge-widget__balance">
            {isBalanceLoading
              ? "Loading..."
              : finalUsdcBalance !== null
              ? finalUsdcBalance.toFixed(2)
              : "0.00"}{" "}
            {TOKEN_SYMBOLS[direction].source}
          </span>
        </div>
        {sourceChainId === "1" && (
          <div>
            Your SOL Balance:{" "}
            <span className="bridge-widget__balance">
              {isBalanceLoading
                ? "Loading..."
                : solanaSolBalance !== null
                ? solanaSolBalance.toFixed(6)
                : "0.00"}{" "}
              SOL
            </span>
          </div>
        )}
        {direction === "fromBESC" && !poolStatus.withdrawalsEnabled && (
          <motion.div
            className="bridge-widget__warning"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            Withdrawals are temporarily limited. Try a smaller amount or check back later.
          </motion.div>
        )}
      </div>
      <button
        onClick={toggleDirection}
        className="bridge-widget__button"
        disabled={loading}
        aria-label={`Toggle bridge direction to ${
          direction === "toBESC" ? "from BESC" : "to BESC"
        }`}
      >
        {direction === "toBESC" ? (
          <>
            Bridge to BESC <span>→</span>
          </>
        ) : (
          <>
            Bridge from BESC <span>←</span>
          </>
        )}
      </button>
      <div className="bridge-widget__section bridge-widget__chain-section">
        <div className="bridge-widget__field">
          <label htmlFor="source-chain" className="bridge-widget__label">
            From
          </label>
          <select
            id="source-chain"
            value={sourceChainId}
            onChange={(e) => {
              setSourceChainId(e.target.value);
              if (e.target.value === "1") {
                setWalletChainId(null);
                setSolanaUsdcBalance(null);
                setSolanaSolBalance(null);
                if (isSolanaConnected && !solanaAddress) {
                  reconnectWallet();
                }
              } else if (isSolanaConnected) {
                setSolanaUsdcBalance(null);
                setSolanaSolBalance(null);
              }
            }}
            disabled={loading || switchChainLoading}
            className="bridge-widget__select"
          >
            {direction === "toBESC" ? (
              Object.entries(CHAIN_CONFIGS)
                .filter(([id]) => id !== "2372")
                .map(([id, config]) => (
                  <option key={id} value={id}>
                    {config.name}
                  </option>
                ))
            ) : (
              <option value="2372">{CHAIN_CONFIGS[2372].name}</option>
            )}
          </select>
        </div>
        <div className="bridge-widget__field">
          <label htmlFor="destination-chain" className="bridge-widget__label">
            To
          </label>
          <select
            id="destination-chain"
            value={destinationChainId}
            onChange={(e) => setDestinationChainId(e.target.value)}
            disabled={loading}
            className="bridge-widget__select"
          >
            {direction === "toBESC" ? (
              <option value="2372">{CHAIN_CONFIGS[2372].name}</option>
            ) : (
              Object.entries(CHAIN_CONFIGS)
                .filter(([id]) => id !== "2372")
                .map(([id, config]) => (
                  <option key={id} value={id}>
                    {config.name}
                  </option>
                ))
            )}
          </select>
        </div>
      </div>
      <div className="bridge-widget__section">
        <label htmlFor="amount-input" className="bridge-widget__label">
          Amount
        </label>
        <div className="bridge-widget__amount">
          <input
            id="amount-input"
            type="number"
            value={amount}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*\.?\d*$/.test(value)) {
                setAmount(value);
              }
            }}
            placeholder="0.00"
            disabled={loading}
            min="0.01"
            className="bridge-widget__input bridge-widget__amount-input"
            aria-label={`Amount in ${TOKEN_SYMBOLS[direction].source}`}
          />
          <span className="bridge-widget__amount-suffix">
            {TOKEN_SYMBOLS[direction].source}
          </span>
        </div>
      </div>
      {sourceChainId === "1" && direction === "toBESC" && (
        <div className="bridge-widget__section">
          <label htmlFor="besc-address-input" className="bridge-widget__label">
            BESC Address for BUSDC
          </label>
          <div className="bridge-widget__amount">
            <input
              id="besc-address-input"
              type="text"
              value={bescAddress}
              onChange={(e) => {
                const value = e.target.value.trim();
                setBescAddress(value);
                if (value && !ethers.utils.isAddress(value)) {
                  setBescAddressError("Please enter a valid BESC address (e.g., 0x...)");
                } else {
                  setBescAddressError("");
                }
              }}
              placeholder="Enter BESC address (e.g., 0x...)"
              disabled={loading}
              className={`bridge-widget__input bridge-widget__amount-input ${
                                bescAddressError ? "bridge-widget__input--error" : ""
              }`}
              aria-label="BESC address for BUSDC minting"
            />
          </div>
          {bescAddressError && (
            <div className="bridge-widget__error">{bescAddressError}</div>
          )}
        </div>
      )}
      {sourceChainId === "2372" && destinationChainId === "1" && (
        <div className="bridge-widget__section">
          <label htmlFor="solana-address-input" className="bridge-widget__label">
            Solana Address for USDC
          </label>
          <div className="bridge-widget__amount">
            <input
              id="solana-address-input"
              type="text"
              value={solanaAddressInput}
              onChange={(e) => {
                const value = e.target.value.trim();
                setSolanaAddressInput(value);
                if (value && !isValidSolanaAddress(value)) {
                  setSolanaAddressError("Please enter a valid Solana address");
                } else {
                  setSolanaAddressError("");
                }
              }}
              placeholder="Enter Solana address"
              disabled={loading}
              className={`bridge-widget__input bridge-widget__amount-input ${
                solanaAddressError ? "bridge-widget__input--error" : ""
              }`}
              aria-label="Solana address for USDC withdrawal"
            />
          </div>
          {solanaAddressError && (
            <div className="bridge-widget__error">{solanaAddressError}</div>
          )}
        </div>
      )}
      <div className="bridge-widget__section bridge-widget__fee-section">
        <div>
          Cross-Chain Bridge Fee (0.25%): ${(parseFloat(amount || 0) * 0.0025).toFixed(2)}
        </div>
        <div>
          Operator Fee (1% or $5 min): ${Math.max(5, parseFloat(amount || 0) * 0.01).toFixed(2)}
        </div>
        <div>Centralized Pool Gas Fee: $0.50</div>
        <div className="bridge-widget__fee-total">Total Fee: ${fee.toFixed(6)}</div>
        <div className="bridge-widget__fee-total">
          You Send: ${(parseFloat(amount || 0) + fee).toFixed(2)} {TOKEN_SYMBOLS[direction].source}
        </div>
        <div className="bridge-widget__fee-total">
          You Receive: ${parseFloat(amount || 0).toFixed(2)} {TOKEN_SYMBOLS[direction].dest}
        </div>
      </div>
      <button
        onClick={handleBridge}
        disabled={isButtonDisabled}
        className="bridge-widget__button"
        aria-label={`Bridge ${TOKEN_SYMBOLS[direction].source} to ${TOKEN_SYMBOLS[direction].dest}`}
      >
        {loading ? (
          <div className="bridge-widget__loader-container">
            <svg className="bridge-widget__loader" viewBox="0 0 24 24">
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
                opacity="0.25"
              />
              <path
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                opacity="0.75"
              />
            </svg>
            Processing...
          </div>
        ) : (
          `Bridge ${TOKEN_SYMBOLS[direction].source} → ${TOKEN_SYMBOLS[direction].dest}`
        )}
      </button>
      <AnimatePresence>
        {(error || bridgeStatus || buttonMessage) && (
          <motion.div
            className={`bridge-widget__status ${
              error || buttonMessage
                ? "bridge-widget__status--error"
                : "bridge-widget__status--success"
            }`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
          >
            {buttonMessage || error || bridgeStatus}
          </motion.div>
        )}
      </AnimatePresence>
      {showProgressModal && (
        <motion.div
          className="bridge-widget__modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="bridge-widget__modal-content">
            <p className="warning">Do Not Close This Page!</p>
            <p>{bridgeStatus}</p>
            <p>Your transaction is being processed. Closing the page may prevent completion.</p>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default BridgeWidget;
